// Calls the room-layout models through the avatar backend (POST /api/layout →
// the isolated layout-service on avatar-prod). The models only run on prod, so
// dev4 / test admins call prod too; `?layoutApi=<origin>` overrides it.
import HelpService from 'lib/service/help.service';

const DEFAULT_API = 'https://avatar.eyespy360.com';

// Corner Lab detectors (pano-flooplan), best first; Consensus is preselected.
export const LAYOUT_MODELS = [
  { id: 'consensus', label: 'Consensus', hint: 'median of all detectors, right angles' },
  { id: 'bi_tuned_enclosed', label: 'Bi-Layout · EyeSpy tuned, enclosed', hint: 'fine-tuned on our panoramas' },
  { id: 'bi_tuned_extended', label: 'Bi-Layout · EyeSpy tuned, extended' },
  { id: 'bi_enclosed', label: 'Bi-Layout · enclosed', hint: 'closes the room at doorways' },
  { id: 'bi_extended', label: 'Bi-Layout · extended', hint: 'walls continue through openings' },
  { id: 'dopnet_zind', label: 'DOPNet' },
  { id: 'lgt_zind', label: 'LGT-Net' },
  { id: 'horizonnet_zind', label: 'HorizonNet' },
];

export function layoutApiBase() {
  const override = new HelpService().urlParams.layoutApi;
  return override && /^https:\/\/[\w.-]+(:\d+)?$/.test(override) ? override : DEFAULT_API;
}

// Absolute https URL of a scene panorama. Scene configs carry it relative
// ("/u/<uuid>/m/equirectangular.jpg?companyID=1"); the admin domain doesn't
// serve those files (404) — any host of that environment's tour domain does:
// dev4.e3.eyespy360.com → www.dev4.vr-360-tour.com, test.… → www.test.…,
// e3.eyespy360.com → www.vr-360-tour.com.
export function tourAssetOrigin(pageUrl) {
  try {
    const { hostname } = new URL(pageUrl);
    const m = hostname.match(/^(?:(.+)\.)?e3\.eyespy360\.com$/);
    if (m) return `https://www.${m[1] ? m[1] + '.' : ''}vr-360-tour.com`;
  } catch (_) {}
  return pageUrl;
}

export function absolutePanoramaUrl(panorama, base = window.location.href) {
  if (!panorama || typeof panorama !== 'string') return null;
  try {
    const url = new URL(panorama, tourAssetOrigin(base));
    return url.protocol === 'https:' ? url.href : null;
  } catch (_) {
    return null;
  }
}

// FastAPI sends `detail` as a string, or as a list of {msg} on a 422.
function errorText(detail) {
  if (Array.isArray(detail)) return detail.map(d => (d && d.msg) || String(d)).join('; ');
  return typeof detail === 'string' ? detail : '';
}

export async function detectLayout(imageUrl, models, { fetchFn = fetch, base = layoutApiBase() } = {}) {
  const res = await fetchFn(`${base}/api/layout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image_url: imageUrl, models }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(errorText(data.detail) || data.error || `layout request failed (${res.status})`);
  return data.results || {};
}
